import {
  parse, parseISO,
  format, formatISO,
  isValid,
  startOfHour,
  startOfDay, endOfDay,
  isEqual, compareAsc,
  isFuture, isPast,
  addSeconds, addMinutes, addDays, addMonths,
  setHours, setMinutes, setSeconds
} from 'date-fns';


import nlLocale from 'date-fns/locale/nl';
import frLocale from 'date-fns/locale/fr';
import enLocale from 'date-fns/locale/en-GB';
import deLocale from 'date-fns/locale/de';
import csLocale from 'date-fns/locale/cs';

export function _getDefaultDate(date) {
  return isValid(date) ? date : null;
}

export function _parseDateIso(value) {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return _getDefaultDate(value);
  }
  let date = parseISO(value);
  return _getDefaultDate(date);
}

export function _parseDateFormat(value, dateFormat) {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return _getDefaultDate(value);
  }
  let date = parse(value, dateFormat || 'dd-MM-yyyy', new Date());
  return _getDefaultDate(date);
}

export function _formatDate(value, dateFormat, defaultValue, locale) {
  if (!dateFormat) {
    console.error('formatDate method call missing dateFormat argument.');
    return (defaultValue || null);
  }
  let date = _parseDateIso(value);
  if (!date) {
    return (defaultValue || null);
  }

  return format(date, dateFormat, locale ? { locale } : null);
}

export function _getDateFnLocale(locale) {
  if (!locale) {
    return null;
  }
  var currentLocale = locale.substring(0, 2);
  switch (currentLocale) {
    case 'nl': return nlLocale;
    case 'fr': return frLocale;
    case 'en': return enLocale;
    case 'de': return deLocale;
    case 'cs': return csLocale;
    default: return null;
  }
}

export const dateTimeService = {
  methods: {
    parseDateIso(value) {
      return _parseDateIso(value);
    },
    parseDateFormat(value, dateFormat) {
      return _parseDateFormat(value, dateFormat);
    },
    addSecondsToDate(value, seconds) {
      let date = this.parseDateIso(value);
      return date ? addSeconds(date, seconds) : null;
    },
    addMinutesToDate(value, minutes) {
      let date = this.parseDateIso(value);
      return date ? addMinutes(date, minutes) : null;
    },
    addDaysToDate(value, days) {
      let date = this.parseDateIso(value);
      return date ? addDays(date, days) : null;
    },
    addMonthsToDate(value, months) {
      let date = this.parseDateIso(value);
      return date ? addMonths(date, months) : null;
    },
    startOfDate(value) {
      var date = this.parseDateIso(value);
      return date ? startOfDay(date) : null;
    },
    startOfHour(value) {
      var date = this.parseDateIso(value);
      return date ? startOfHour(date) : null;
    },
    endOfDate(value) {
      var date = this.parseDateIso(value);
      return date ? endOfDay(date) : null;
    },
    areDatesEqual(date1, date2) {
      let dateLeft = this.parseDateIso(date1);
      let dateRight = this.parseDateIso(date2);
      if (!dateLeft || !dateRight) {
        return false;
      }
      return isEqual(dateLeft, dateRight);
    },
    sortDatesAsc(date1, date2) {
      let dateLeft = this.parseDateIso(date1);
      let dateRight = this.parseDateIso(date2);
      if (dateLeft && dateRight) {
        return compareAsc(dateLeft, dateRight);
      }
      if (!dateLeft) {
        return 1;
      } else {
        return -1;
      }
    },
    isDateInFuture(value) {
      var date = this.parseDateIso(value);
      return date ? isFuture(date) : false;
    },
    isDateInPast(value) {
      var date = this.parseDateIso(value);
      return date ? isPast(date) : false;
    },
    setDateHours(value, hours) {
      var date = this.parseDateIso(value);
      return date ? setHours(date, hours) : null;
    },
    setDateMinutes(value, minutes) {
      var date = this.parseDateIso(value);
      return date ? setMinutes(date, minutes) : null;
    },
    setDateSeconds(value, seconds) {
      var date = this.parseDateIso(value);
      return date ? setSeconds(date, seconds) : null;
    },
    setDateTime(value, time) {
      var date = this.parseDateIso(value);
      return date ? parse(time, 'HH:mm', date) : null;
    },
    isoDateToUtc(isoDate) {
      if (!isoDate) {
        return null;
      }
      if (isoDate instanceof Date) {
        isoDate = this.formatDateIso(isoDate);
      }
      let parts = isoDate.split('-');
      let date = new Date(Date.UTC(Number.parseInt(parts[0]), Number.parseInt(parts[1] - 1), Number.parseInt(parts[2]), 0, 0, 0));
      return isValid(date) ? date : null;
    },
    isoDateToLocal(isoDate) {
      if (!isoDate) {
        return null;
      }
      var utcDate = this.parseDateIso(isoDate);
      return isValid(utcDate) ? this.addMinutesToDate(utcDate, utcDate.getTimezoneOffset()) : null;
    },
    formatDate(value, dateFormat, defaultValue, locale) {
      locale = locale || this.getDateFnLocale();
      if (!dateFormat) {
        dateFormat = "dd-MM-yyyy"; 
      }
      return _formatDate(value, dateFormat, defaultValue, locale);
    },
    formatTime(value, dateFormat, defaultValue, locale) {
      return this.formatDate(value, (dateFormat || 'HH:mm'), defaultValue, locale);
    },
    checkIsValid(value) {
      return isValid(value)
    },
    getDateFnLocale() {
      if (!this.$i18n) {
        return null;
      }
      return _getDateFnLocale(this.$i18n.locale);
    },
    formatDateIso(value, defaultValue) {
      var date = this.parseDateIso(value);
      return date ? formatISO(date) : (defaultValue || null);
    },
    formatDateToIsoString(value, defaultValue) {
      var date = this.parseDateIso(value);
      return date ? date.toISOString() : (defaultValue || null);
    },
    toJsonWithoutOffset(date) {
      return format(date, 'yyyy-MM-dd\'T\'HH:mm');
    },
    getTodayIso() {
      return formatISO(this.startOfDate(new Date()));
    },
    getTodayUtc() {
      return this.isoDateToUtc(this.startOfDate(new Date()));
    },
  }
};

export const formats = {
  "nl-NL": {
    short: {
      format: "dd-MM-yyyy",
      mask: "##-##-####",
      placeholder: "dd-mm-yyyy",
      regex: /((0[1-9]|[12]\d|3[01])-(0[1-9]|1[0-2])-[12]\d{3})$/
    },
  },
  "fr-FR": {
    short: {
      format: "dd/MM/yyyy",
      mask: "##/##/####",
      placeholder: "dd/mm/yyyy",
      regex: /((0[1-9]|[12]\d|3[01])\/(0[1-9]|1[0-2])\/[12]\d{3})$/
    },
  },
  "en-GB": {
    short: {
      format: "dd/MM/yyyy",
      mask: "##/##/####",
      placeholder: "dd/mm/yyyy",
      regex: /((0[1-9]|[12]\d|3[01])\/(0[1-9]|1[0-2])\/[12]\d{3})$/
    },
  },
  "de-DE": {
    short: {
      format: "dd.MM.yyyy",
      mask: "##.##.####",
      placeholder: "dd.mm.yyyy",
      regex: /((0[1-9]|[12]\d|3[01]).(0[1-9]|1[0-2]).[12]\d{3})$/
    },
  },
}